// External dependencies
import { ErrorMessage } from '@hookform/error-message';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

// Utilities
import { _isEmpty, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';

// Model types
import type { ID } from '@/model/Common.model';
import type { MetadataFieldWithValue } from '@/model/Metadata.model';
import type { Project } from '@/model/Project.model';

// Support modules
import { renderMetadataValue } from '@/helpers';
import { useFetchCollection } from '@/support/Hooks/fetch';
import { notAborted, useAbortController } from '@/support/Hooks/fetch/useAbortController';
import useMountedState from '@/support/Hooks/fetch/useMountedState';
import Http from '@/support/http';
import { api as apiRoute } from '@/support/route';

// Internal components
import ApiErrorBanner, { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import Lookup from '@/components/UI/Lookup';
import RemoveButton from '@/components/UI/RemoveButton';
import Table from '@/components/UI/Table';
import type { TanStackColumns } from '@/components/UI/Table/TableComponent.model';

interface ProjectRole {
  user: { name: string };
  role: { name: string };
}

interface ProjectWithRoles extends Omit<Project, 'metadata'> {
  roles?: Array<ProjectRole>;
  metadata?: Array<MetadataFieldWithValue>;
}

interface ProjectsFieldProps {
  setProjectNameForSummary: (name: string) => void;
}

interface ProjectOption {
  label: string;
  value: ID;
}

interface LoadOptionsParams {
  inputValue: string;
  page: number;
}

interface ProjectRoleDisplay {
  name: string;
  role: string;
}

const projectRolesTableColumns: TanStackColumns<ProjectRoleDisplay> = [
  {
    id: 'role',
    header: 'Role',
    accessorKey: 'role',
    enableSorting: true,
  },
  {
    id: 'name',
    header: 'Name',
    accessorKey: 'name',
    enableSorting: true,
  },
];

const projectMetadataTableColumns: TanStackColumns<MetadataFieldWithValue> = [
  {
    id: 'title',
    header: 'Name',
    accessorKey: 'title',
    enableSorting: true,
  },
  {
    id: 'value',
    header: 'Value',
    accessorKey: 'value',
    enableSorting: true,
    cell: ({ row: { original } }) => {
      return <>{renderMetadataValue(original)}</>;
    },
  },
];

const ProjectMetadataTable: FC<{ metadata: Array<MetadataFieldWithValue> }> = ({ metadata = [] }) => (
  <Table columns={projectMetadataTableColumns} data={metadata} />
);

const ProjectRolesTable: FC<{ roles: Array<ProjectRoleDisplay> }> = ({ roles = [] }) => (
  <Table columns={projectRolesTableColumns} data={roles} />
);

const ProjectsField: FC<ProjectsFieldProps> = ({ setProjectNameForSummary }) => {
  const {
    collection: initialProjects,
    collectionLoading: initialProjectsLoading,
    collectionError: initialProjectsError,
  } = useFetchCollection({
    collectionType: 'projects',
  });

  const [projectsError, setProjectsError] = useState<Error | null>(null);

  const [selectedProject, setSelectedProject] = useState<ProjectWithRoles | undefined>(undefined);
  const [selectedProjectLoading, setSelectedProjectLoading] = useState(false);
  const [selectedProjectError, setSelectedProjectError] = useState<Error | null>(null);

  const { newAbortController: newProjectGetAbortController } = useAbortController();
  const { newAbortController: newLoadOptionsAbortController } = useAbortController();
  const isMounted = useMountedState();

  const {
    control,
    formState: { errors },
  } = useFormContext();
  const projectIdWatch = useWatch({
    control,
    name: 'project_id',
  });

  const fetchProject = async (projectId: ID) => {
    try {
      setSelectedProjectLoading(true);

      const {
        data: { data },
      } = await Http.get(apiRoute('projects.get', { projectId }), {
        signal: newProjectGetAbortController().signal,
      });
      if (isMounted()) {
        setSelectedProject(data);
        setSelectedProjectLoading(false);
      }
    } catch (error) {
      if (isMounted() && notAborted(error)) {
        setSelectedProjectError(error as Error);
        setSelectedProjectLoading(false);
      }
    }
  };

  const loadOptions = async ({ inputValue, page }: LoadOptionsParams) => {
    // This could be called by AsyncPaginate after component has unmounted.
    if (!isMounted()) {
      return;
    }

    try {
      let url = apiRoute('projects.list');
      url = `${url}?page=${page}`;

      if (_isNotBlank(inputValue)) {
        url = `${url}&query=${inputValue}`;
      }

      const {
        data: { data, meta },
      } = await Http.get(url, { signal: newLoadOptionsAbortController().signal });
      if (isMounted()) {
        const { current_page: currentPage, last_page: lastPage } = meta;
        const hasMore = currentPage < lastPage;
        return {
          options: data.map((item: Project) => ({
            label: item.name,
            value: item.id,
          })),
          hasMore,
        };
      }
      return {};
    } catch (error) {
      if (isMounted() && notAborted(error)) {
        setProjectsError(error as Error);
      }
    }
  };

  useEffect(() => {
    if (_notNil(projectIdWatch) && _isNotBlank(String(projectIdWatch))) {
      fetchProject(projectIdWatch);
    }
  }, [projectIdWatch]);

  useEffect(() => {
    setProjectNameForSummary(selectedProject?.name ?? '');
  }, [selectedProject, setProjectNameForSummary]);

  const getDefaultValue = () => {
    if (_notNil(selectedProject)) {
      return {
        label: selectedProject.name,
        value: selectedProject.id,
      };
    }
  };

  const getValue = (projectId: ID | undefined): ProjectOption | '' => {
    if (_notNil(projectId) && _notNil(selectedProject)) {
      return {
        label: selectedProject.name,
        value: selectedProject.id,
      };
    }
    return '';
  };

  const projectRolesData = useMemo((): Array<ProjectRoleDisplay> => {
    if (_notNil(selectedProject) && _isNotEmpty(selectedProject.roles)) {
      return selectedProject.roles.map((role: ProjectRole) => ({
        name: role.user.name,
        role: role.role.name,
      }));
    }
    return [];
  }, [selectedProject]);

  if (initialProjectsLoading) {
    return null;
  }

  if (_isEmpty(initialProjects)) {
    return null;
  }

  if (initialProjectsError !== false) {
    return <ApiErrorBanner errorType={ApiErrorType.FETCH} error={initialProjectsError} />;
  }

  return (
    <>
      <Controller
        name="project_id"
        control={control}
        defaultValue={projectIdWatch}
        rules={{
          required: 'Project ID is required',
        }}
        render={({ field }) => (
          <>
            {_notNil(projectsError) && <ApiErrorBanner errorType={ApiErrorType.FETCH} error={projectsError} />}
            <div>
              <label>Select a project</label>
            </div>
            <div className="flex justify-between hide-child">
              <div className="w-100">
                <Lookup
                  value={getValue(field.value)}
                  placeholder="Search or select..."
                  defaultValue={getDefaultValue()}
                  loadOptions={loadOptions}
                  handleSelect={(data: ProjectOption) => field.onChange(data.value)}
                  name="project"
                />
                <ErrorMessage
                  errors={errors}
                  name="project_id"
                  render={({ message }) => <p className="f6 red db pt2">{message}</p>}
                />
              </div>
              {_notNil(selectedProject) && (
                <RemoveButton
                  className="ml2 child"
                  onClick={() => {
                    field.onChange('');
                    setSelectedProject(undefined);
                  }}
                />
              )}
            </div>
          </>
        )}
      />

      <div className={`${selectedProjectLoading ? 'ui__disabled' : ''}`}>
        {_notNil(selectedProjectError) && (
          <ApiErrorBanner errorType={ApiErrorType.FETCH} error={selectedProjectError} />
        )}
        {_notNil(selectedProject) ? (
          <>
            {_isNotEmpty(selectedProject.metadata) && (
              <>
                <h4 className="f6 lh-title mv3">Metadata</h4>
                <ProjectMetadataTable metadata={selectedProject.metadata} />
              </>
            )}
            {_isNotEmpty(selectedProject.roles) && (
              <>
                <h4 className="f6 lh-title mv3">Roles</h4>
                <ProjectRolesTable roles={projectRolesData} />
              </>
            )}
          </>
        ) : null}
      </div>
    </>
  );
};

export default ProjectsField;
